/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2022 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2022 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { TFunction } from 'i18next';
import type { FieldError, FieldErrors } from 'react-hook-form';
import type { CustomErrorField } from './types';
import type { ForecastType } from '../../types';

export const getCustomError = (
  error: FieldError,
  isChangeGroup: boolean,
  rowIndex: number,
): CustomErrorField => {
  return {
    ...error,
    isChangeGroup,
    rowIndex,
  };
};

export const getIssuesLabel = (sumOfErrors: number, t: TFunction): string => {
  switch (sumOfErrors) {
    case 0:
      return t('issues-button-0');
    case 1:
      return t('issues-button-1');
    default:
      return t('issues-button-multiple', { amount: sumOfErrors });
  }
};

export const getErrorValues = (
  fieldErrors: FieldErrors,
  isChangeGroup = false,
  rowIndex = -1,
): FieldError[] => {
  if (!fieldErrors) {
    return [];
  }
  return Object.keys(fieldErrors).reduce(
    (list: FieldError[], key: keyof FieldErrors) => {
      const error = { ...fieldErrors[key] } as FieldError | FieldErrors;

      if (error.ref) {
        return list.concat(
          getCustomError(error as FieldError, isChangeGroup, rowIndex),
        );
      }

      return list.concat(
        getErrorValues(error as FieldErrors, isChangeGroup, rowIndex),
      );
    },
    [],
  );
};

export const filterDuplicateErrors = (
  errors: CustomErrorField[],
): CustomErrorField[] =>
  errors.reduce((list: CustomErrorField[], error) => {
    // check on type validateRequiredSub and validateRequiredMainOptional since these are giving 'duplicates'
    if (error.type === 'validateRequiredSub') {
      const exist = list.find(
        (e) =>
          e.type === 'validateRequiredSub' && e.rowIndex === error.rowIndex,
      );
      return exist ? list : list.concat(error);
    }
    if (error.type === 'validateRequiredMainOptional') {
      const exist = list.find(
        (e) =>
          e.type === 'validateRequiredMainOptional' &&
          e.rowIndex === error.rowIndex,
      );
      return exist ? list : list.concat(error);
    }
    return list.concat(error);
  }, []);

export const getErrors = (errors: FieldErrors): CustomErrorField[] => {
  const errorList = Object.keys(errors).reduce<FieldError[]>((list, key) => {
    const errorValue = errors[key] as FieldErrors | FieldErrors[];
    // changeGroups
    if (Array.isArray(errorValue)) {
      const errorValues = errorValue.reduce(
        (
          changeGroupErrors: FieldError[],
          changeGroupError: FieldErrors,
          index: number,
        ) =>
          changeGroupErrors.concat(
            getErrorValues(changeGroupError, true, index),
          ),
        [],
      ) as FieldError[];
      return list.concat(...errorValues);
    }
    // baseForecast
    return list.concat(getErrorValues(errorValue as FieldErrors, false, -1));
  }, []);
  return errorList as CustomErrorField[];
};

// The four baseforecast "required" error messages produced by
// `validateRequiredMainFields` in TafFormRow when the baseforecast is empty.
// The form is considered "empty" when all four are present.
const NAF_EMPTY_REQUIRED_MESSAGES = [
  'Wind is required',
  'Visibility is required',
  'Weather is required',
  'Clouds is required',
];

/**
 * Returns a synthesized hint entry that informs the user an empty NAF can
 * still be published by entering a remark starting with "NO NAF". The hint
 * is only returned when:
 *  - forecastType is 'NAF'
 *  - all four baseforecast required errors are present (truly empty form)
 *  - the remark does not already start with "NO NAF"
 *
 * Returns null otherwise.
 */
export const getNafEmptyHint = (
  errors: CustomErrorField[],
  forecastType: ForecastType | undefined,
  remark: string | undefined,
  hintMessage: string,
): CustomErrorField | null => {
  if (forecastType !== 'NAF') {
    return null;
  }

  if (remark?.toUpperCase().trim().startsWith('NO NAF')) {
    return null;
  }

  const baseForecastRequiredMessages = errors
    .filter(
      (error) =>
        !error.isChangeGroup && error.type === 'validateRequiredMainFields',
    )
    .map((error) => error.message);

  const hasAllRequiredErrors = NAF_EMPTY_REQUIRED_MESSAGES.every((message) =>
    baseForecastRequiredMessages.includes(message),
  );

  if (!hasAllRequiredErrors) {
    return null;
  }

  return {
    message: hintMessage,
    type: 'nafEmptyHint',
    ref: { name: 'nafEmptyHint' },
    isChangeGroup: false,
    rowIndex: -1,
    isHint: true,
  };
};
